<template>
  <div class="paginator-wrapper" v-if="records.length && totalPages > 0">
    <ul class="paginator">
      <li
        class="paginator-btn"
        :class="{ 'paginator-btn--disabled': currentPage <= 1 }"
        @click="handlePrev"
      >
        <i class="pi pi-angle-left"></i>
      </li>

      <li
        v-for="page in visiblePages"
        :key="page"
        class="paginator-btn"
        :class="{ 'paginator-btn--active': page === currentPage }"
        @click="goToPage(page)"
      >
        {{ page }}
      </li>

      <li
        class="paginator-btn"
        :class="{ 'paginator-btn--disabled': currentPage >= totalPages }"
        @click="handleNext"
      >
        <i class="pi pi-angle-right"></i>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  records: { type: Array, required: true },
  totalRecords: { type: Number, required: true },
  pageSize: { type: Number, required: true },
  currentPage: { type: Number, required: true },
  windowSize: { type: Number, default: 4 },
});

const emit = defineEmits(["prevPage", "nextPage", "goToPage"]);

const totalPages = computed(() => Math.ceil(props.totalRecords / props.pageSize));

const visiblePages = computed(() => {
  const total = totalPages.value;
  const win = props.windowSize;

  if (total <= win) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const seriesIndex = Math.ceil(props.currentPage / win);
  const start = (seriesIndex - 1) * win + 1;
  const end = Math.min(start + win - 1, total);

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const handlePrev = () => {
  if (props.currentPage > 1) emit("prevPage");
};

const handleNext = () => {
  if (props.currentPage < totalPages.value) emit("nextPage");
};

const goToPage = (page) => {
  emit("goToPage", page);
};
</script>

<style scoped>
.paginator-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.paginator {
  display: flex;
  list-style: none;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 0;
  border: 1px solid #dfe4ea;
  border-radius: 12px;
}

.paginator-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 0.5rem;
  border: 1px solid #dfe4ea;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #637381;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s;
}

.paginator-btn:hover:not(.paginator-btn--active):not(.paginator-btn--disabled) {
  background: #f3f4f6;
}

.paginator-btn--active {
  background-color: #3758f9;
  color: white;
  border-color: #3758f9;
  cursor: default;
}

.paginator-btn--disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
